import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {AbstractSheetController} from '@mintdata-wsp/common/controllers/spreadsheet/AbstractSheetController';
import {IMeshContext} from '@mintdata-wsp/common/controllers/spreadsheet/mesh/contexts/MeshContext';
import {BindingRuntimeState} from '@mintdata-wsp/common/state/ui/visualDesigner/BindingState';
import {NamedRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/NamedRange';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {SlotUtil} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotUtil';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {
    BindingDirection,
    EventBinding,
    IBinding,
    PropBinding,
    RepeaterFixation
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/binding/Binding';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {EventProp} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/EventProp';
import {IProperty, Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {EnumUtils} from '@mintdata-wsp/common/util/EnumUtil';
import {ISheetLocation} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {BindingId, SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {FieldName, FrameCellCoordinate, FramePath, IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {JsonUtil} from '@mintdata/mint-kit/impl/util/JsonUtil';
import bind from 'bind-decorator';
import _times from 'lodash/times';
import {action} from 'mobx';

const bindingDirectionMap = new Map([
    ['down', BindingDirection.Down],
    ['right', BindingDirection.Right]
]);

const repeaterFixationMap = new Map([
    ['fixed', RepeaterFixation.FixedBoth],
    ['down', RepeaterFixation.FixedDown],
    ['right', RepeaterFixation.FixedRight]
]);

export const isInInterval = (point, start, length) => (point >= start && point < start + length);

export class BindingsController
{
    @action.bound
    public createEventBinding(eventProp:EventProp, workbookId:WorkbookId, sheetId:SheetId, location:SlotLocation, framePath:FramePath, repeaterFixation:RepeaterFixation)
    {
        const {namedRanges} = commonControllers.spreadsheets;

        const rangeName = null;
        const namedRange = namedRanges.createNamedRange(workbookId, sheetId, location, framePath, 1, 1, rangeName);

        const eventBinding = EventBinding.create(
            eventProp.box,
            eventProp,
            namedRange,
            repeaterFixation
        );

        eventProp.setIsEnabled(true);
        this.bindProperty(eventProp, eventBinding);
    }

    @action.bound
    public createPropBinding(prop:Prop<any>, propPath:PropPath,
                             workbookId:WorkbookId, sheetId:SheetId, location:SlotLocation, framePath:FramePath,
                             rangeRowCount:number, rangeColCount:number,
                             repeaterFixation:RepeaterFixation,
                             bindingDirection:BindingDirection,
                             useFormat:boolean)
    {
        const {namedRanges} = commonControllers.spreadsheets;

        const rangeName = null;
        const namedRange = namedRanges.createNamedRange(workbookId, sheetId, location, framePath, rangeRowCount, rangeColCount, rangeName);

        const propBinding = PropBinding.create(
            prop.box,
            prop,
            propPath,
            namedRange,
            repeaterFixation,
            bindingDirection,
            useFormat
        );

        this.bindProperty(prop, propBinding);
    }

    @action.bound
    public onNamedRangeRemoved(range:NamedRange)
    {
        Array.from(this.getRuntimeState().bindings.values())
            .filter(binding => binding.range.id === range.id)
            .forEach(binding => this.removeBinding(binding.prop, binding));

        this._clearCurrentBindingViewState();
    }

    @action.bound
    public clearBindingsForRange(sheetId:SheetId, startRow:number, startCol:number, rowCount:number, colCount:number)
    {
        Array.from(this.getRuntimeState().bindings.values())
            .filter(binding =>
            {
                const range = binding.range;
                if( sheetId !== range.sheetId ) return false;

                const {rowIndex, colIndex} = range.location;
                return isInInterval(rowIndex, startRow, rowCount) && isInInterval(colIndex, startCol, colCount);
            })
            .forEach(binding => this.removeBinding(binding.prop, binding));

        this._clearCurrentBindingViewState();
    }

    @action.bound
    public unbindProp(prop:Prop<any>, propPath:PropPath)
    {
        const binding = prop.getBinding(propPath);
        if( !binding )
            return;

        const propConfig = prop.getPropertyConfig(propPath);
        if( propConfig && propConfig.spec.keepValueAfterBindingRemoving )
        {
            const currentValue = prop.getPropValue(propPath);
            if( currentValue && !Type.isInstanceOf(currentValue, Type.Error) )
                prop.setPropValue(propPath, currentValue);
            else
                console.warn('Prop removeBinding value is Error:', propPath, currentValue);
        }

        this.removeBinding(prop, binding);
        this._clearCurrentBindingViewState();
    }

    @action.bound
    public unbindEventProp(eventProp:EventProp)
    {
        this.removeBindingById(eventProp, eventProp.bindingId);
        this._clearCurrentBindingViewState();
    }

    @action.bound
    public removeBindingById(prop:IProperty, bindingId:BindingId):boolean
    {
        const binding = this.getBinding(bindingId);
        if( !binding )
            return false;

        this.removeBinding(prop, binding);
    }

    @action.bound
    public removeBinding(prop:IProperty, binding:IBinding):boolean
    {
        const {namedRanges} = commonControllers.spreadsheets;

        if( !prop.removeBinding(binding.id) )
            return false;

        // remove anonymous ranges right away...
        if( !binding.range.name )
            namedRanges.removeNamedRange(binding.range.id);

        this.getRuntimeState().removeBinding(binding.id);
        return true;
    }

    @action.bound
    public moveBindingsOnPropArrayItemMoved(prop:Prop<any>, arrayPath:PropPath, from:number, to:number)
    {
        Assert.yes(from != to, 'Index shouldn\'t be equal');

        const [minIndex, maxIndex] = from < to ? [from, to] : [to, from];

        this._editBindingsForArray(prop, arrayPath, (binding) =>
        {
            const partIndex = arrayPath.length();

            const bindingPropPath = binding.propPath;
            const bindIndex = bindingPropPath.getNumberPartAt(partIndex);

            if( bindIndex < minIndex || bindIndex > maxIndex )
                return;

            let newIndex;
            if( bindIndex == from )
            {
                newIndex = to;
            }
            else if( from < to )
            {
                //moving to the end of array, so other elements are shifted to the start
                newIndex = bindIndex - 1;
            }
            else
            {
                //here: from > to

                //moving to the start of array, so other elements are shifted to the end
                newIndex = bindIndex + 1;
            }

            binding.propPath = bindingPropPath.withPartAt(partIndex, newIndex);
        });
    }

    @action.bound
    public removeBindingsOnPropArrayItemRemoved(prop:Prop<any>, arrayPath:PropPath, index:number)
    {
        this._editBindingsForArray(prop, arrayPath, (binding) =>
        {
            const partIndex = arrayPath.length();

            const bindingPropPath = binding.propPath;
            const bindIndex = bindingPropPath.getNumberPartAt(partIndex);

            if( bindIndex === index )
                this.removeBinding(prop, binding);
            else if( bindIndex > index )
                binding.propPath = bindingPropPath.withPartAt(partIndex, bindIndex - 1);
        });
    }

    @action.bound
    private _editBindingsForArray(prop:Prop<any>, arrayPath:PropPath, consumerFn:(binding:PropBinding) => void)
    {
        this._clearCurrentBindingViewState();

        // need to copy bindingIds before the loop, because it will change `prop.bindingIds` in the proccess
        const bindingIds = [...prop.bindingIds];
        for( const bindingId of bindingIds )
        {
            const binding = this.getPropBinding(bindingId);
            Assert.yes(binding);

            const bindingPropPath = binding.propPath;
            if( arrayPath.isStrictPrefixOf(bindingPropPath) )
                consumerFn(binding);
        }
    }

    protected _clearCurrentBindingViewState():void
    {
    }

    @bind
    public getBinding(bindingId:BindingId):IBinding
    {
        return this.getRuntimeState().getBinding(bindingId);
    }

    @bind
    public getPropBinding(bindingId:BindingId):PropBinding
    {
        return this.getRuntimeState().getPropBinding(bindingId);
    }

    @bind
    public getEventBinding(bindingId:BindingId):EventBinding
    {
        return this.getRuntimeState().getEventBinding(bindingId);
    }

    @bind
    public getPropBoundValue(prop:Prop<any>, binding:PropBinding, repeater:Repeater):any | null
    {
        const {namedRanges} = commonControllers.spreadsheets;

        const propConfig = prop.getPropertyConfig(binding.propPath);
        Assert.yes(propConfig, 'propConfig should be defined here, otherwise - how did we bind?');

        const {propCtor, propType, isArray, isPrimitive, valueWidth} = propConfig;

        const isVertical = binding.isVertical;

        const lengthLimit = isArray ? Number.MAX_VALUE : 1;

        const rowsLimit = isVertical ? lengthLimit : valueWidth;
        const colsLimit = isVertical ? valueWidth : lengthLimit;

        const namedRangeValues = namedRanges.getNamedRangeValues(binding.range.id, repeater, binding.repeaterFixation, binding.useFormat, rowsLimit, colsLimit);
        const isError:boolean = (namedRangeValues as IValue).isError;

        let rangeValues:Array<Array<IValue>>;

        rangeValues = isError ? [[namedRangeValues as IValue]] : (namedRangeValues as Array<Array<IValue>>);

        const coercedRangeValueOrNullFn = (toType, toCtor, itemInd, subItemInd) =>
        {
            const row = rangeValues[isVertical ? itemInd : subItemInd];

            let value = row && row[isVertical ? subItemInd : itemInd];
            if( !value || Type.isInstanceOf(value, Type.Error) )
                return null;

            value = CoercionUtils.coerce(value, toType, toCtor);
            if( !value || Type.isInstanceOf(value, Type.Error) )
                return null;

            return value;
        };

        const createItemValueFn = (itemInd) =>
        {
            if( isPrimitive )
                return coercedRangeValueOrNullFn(propType, propCtor, itemInd, 0);

            const value = propCtor.default ? propCtor.default() : new propCtor();

            const subNames = propConfig.propertyNames;
            const subConfigs = propConfig.propertiesConfigs;
            for( let subInd = 0; subInd < valueWidth; ++subInd )
            {
                const row = rangeValues[isVertical ? itemInd : subInd];

                const subName = subNames[subInd];
                const subConfig = subConfigs.get(subName);

                const subValue = coercedRangeValueOrNullFn(subConfig.type, subConfig.typeCtor, itemInd, subInd);

                // only change default subProp, if we have smth meaningfull after coercion
                if( subValue )
                    value[subName] = subValue;
            }

            return value;
        };

        if( !isArray )
            return createItemValueFn(0);
        else if( isError )
            return [];

        const length = isVertical
            ? rangeValues.length
            : rangeValues[0] && rangeValues[0].length || 0;

        return _times(length, itemInd =>
            // in case of primitive arrays => return defaultValue, if coercion was unsuccessfull
            createItemValueFn(itemInd) || CoercionUtils.defaultValue(propType, propCtor)
        );
    }

    @bind
    public updateBoundSlotValue(propBinding:PropBinding, repeater:Repeater, newValue:any, itemIndex?:number, subProp?:string)
    {
        // non-IValue here might only make sense, when setting whole composites and arrays will be implemented
        if( !Type.isInstanceOfIValue(newValue) )
            return;

        const openDesign = commonControllers.designState.getOpenDesign();

        if( !openDesign )
            return false;

        const {prop, propPath, range, repeaterFixation} = propBinding;
        const {sheetId, location, framePath} = range;

        const propConfig = prop.getPropertyConfig(propPath);
        let propOffset = 0;

        if( propConfig.isComposite )
        {
            // setting whole composite objects into sheet isn't yet supported
            if( subProp == null )
                return;

            propOffset = propConfig.propertyNames.indexOf(subProp);

            if( propOffset < 0 )
            {
                console.warn(`Unknown SubProperty name ${subProp} for property: `, prop);
                return;
            }
        }

        let arrayOffset = 0;

        if( propConfig.isArray )
        {
            // setting whole array into sheet isn't yet supported
            if( itemIndex == null )
                return;

            arrayOffset = itemIndex;

            if( arrayOffset < 0 )
            {
                console.error(`Invalid itemIndex ${itemIndex} for prop: `, prop);
                return;
            }
        }

        const sheetCtrl = this._getSheetCtrl(sheetId);

        const {rowIndex, colIndex} = location;
        const slotLocation:SlotLocation = new SlotLocation(colIndex, rowIndex);
        const slotFormula:SlotFormula = sheetCtrl.getSlotFormula(sheetId, slotLocation);

        if( SlotUtil.isFormula(slotFormula) )
        {
            console.warn(`Property bound to Slot with formula. Property: `, prop);
            return;
        }

        if( framePath )
        {
            const slotValue:IFrame = sheetCtrl.getSlotFrameValue(sheetId, slotLocation);

            const nestedFrame:IFrame = framePath.length > 1
                ? slotValue.getCellByPath(framePath.slice(0, -1)) as IFrame
                : slotValue;

            const nestedFieldNames:FieldName[] = nestedFrame.getFieldNames();
            const nestedCellCoordinate:FrameCellCoordinate = framePath[framePath.length - 1];

            let nestedRowIndex:number = nestedCellCoordinate.rowIndex;
            let nestedColIndex:number = nestedFieldNames.indexOf(nestedCellCoordinate.fieldName);

            if( !EnumUtils.isFixedDown(repeaterFixation) )
                nestedRowIndex += repeater.rowOffset;

            if( !EnumUtils.isFixedRight(repeaterFixation) )
                nestedColIndex += repeater.colOffset;

            nestedRowIndex += (propBinding.isVertical ? arrayOffset : propOffset);
            nestedColIndex += (propBinding.isVertical ? propOffset : arrayOffset);

            const newFramePath:FramePath = [
                ...framePath.slice(0, -1),
                new FrameCellCoordinate(nestedFieldNames[nestedColIndex], nestedRowIndex)
            ];

            const newFrame:IFrame = slotValue.withCellByPath(newFramePath, newValue);

            sheetCtrl.setSlotValue(sheetId, new SlotLocation(colIndex, rowIndex), newFrame);
        }
        else
        {
            let {rowIndex, colIndex} = location;

            if( !EnumUtils.isFixedDown(repeaterFixation) )
                rowIndex += repeater.rowOffset;

            if( !EnumUtils.isFixedRight(repeaterFixation) )
                colIndex += repeater.colOffset;

            rowIndex += (propBinding.isVertical ? arrayOffset : propOffset);
            colIndex += (propBinding.isVertical ? propOffset : arrayOffset);

            sheetCtrl.setSlotFormula(sheetId, new SlotLocation(colIndex, rowIndex), newValue, false);
        }
    }

    @bind
    public async reEvaluateLazySlot(eventBinding:EventBinding, repeater:Repeater, eventFrame:IFrame):Promise<boolean>
    {
        const {sheetId, location, framePath} = eventBinding.range;
        if( framePath )
            return false;

        let {rowIndex, colIndex} = location;

        if( !EnumUtils.isFixedRight(eventBinding.repeaterFixation) )
            colIndex += repeater.colOffset;

        if( !EnumUtils.isFixedDown(eventBinding.repeaterFixation) )
            rowIndex += repeater.rowOffset;

        const sheetCtrl = this._getSheetCtrl(sheetId);
        const slot = sheetCtrl.findSlot(sheetId, new SlotLocation(colIndex, rowIndex));
        if( slot && slot.isLazy )
            return await sheetCtrl.reEvaluateLazySlot(slot.sheet.id, slot.location, eventFrame);

        return false;
    }

    @action.bound
    public bindProperty(prop:IProperty, newBinding:IBinding):void
    {
        this.getRuntimeState().addBinding(newBinding);
        prop.addBinding(newBinding.id);
    }

    @bind
    protected _getSheetCtrl(sheetId:SheetId):AbstractSheetController
    {
        return commonControllers.spreadsheets.workbook.getSheetCtrl(sheetId);
    }

    @bind
    protected getRuntimeState():BindingRuntimeState
    {
        let openDesign = commonControllers.designState.getOpenDesign();
        if( !openDesign )
            return;

        return openDesign.designState.binding.runtime;
    }

    public setBinding(pageName:string,
                      componentName:string,
                      propOrEventFullPath:string,
                      sheetName:string,
                      cellLocation:ISheetLocation,
                      pathInFrame:string,
                      fixedDirection:string,
                      useFormat:boolean = false,
                      direction:string,
                      itemsCount:number):boolean | Error
    {
        const page:Page = commonControllers.surfaceArea.findPageByName(pageName);

        if( !page )
            return new Error(`Page with the name ${pageName} doesn't exist`);

        const box:Box = commonControllers.surfaceArea.findBoxByName(page.id, componentName);

        if( !box )
            return new Error(`Component with the name ${componentName} doesn't exist`);

        if( !propOrEventFullPath )
            return new Error(`Property or event name must be specified`);

        const workbook:Workbook = commonControllers.spreadsheets.main.getRootWorkbook();
        const sheet:AnySheet = commonControllers.spreadsheets.workbook.findSheetByName(workbook.id, sheetName);

        if( !sheet )
            return new Error(`Sheet with the name ${sheetName} doesn't exist`);

        const framePathOrError = pathInFrame ? JsonUtil.jsonPathToFramePath(pathInFrame) : null;
        if( framePathOrError instanceof Error )
            return framePathOrError;

        const framePath = framePathOrError as FramePath;

        const ctx:IMeshContext = commonControllers.spreadsheets.workbook.getMeshContextBySheetId(sheet.id);

        const slotLocation = SlotLocation.fromSheetAPI(cellLocation);
        const bindingDirection = bindingDirectionMap.get((direction || '').toLowerCase()) || BindingDirection.Down;
        const repeaterFixation = repeaterFixationMap.get((fixedDirection || '').toLowerCase()) || RepeaterFixation.NotFixed;

        const eventProp:EventProp = box.componentDesc.getEventPropByName(propOrEventFullPath);
        if( eventProp )
            commonControllers.bindings.createEventBinding(eventProp, workbook.id, sheet.id, slotLocation, framePath, repeaterFixation);
        else
        {
            const pathParts = propOrEventFullPath.split('.');
            let propName:string = pathParts[0];

            let prop:Prop<any> = box.componentDesc.baseProperties.get(propName) || box.componentDesc.layoutProperties.get(propName);

            let propPath;
            if( prop )
                propPath = new PropPath(pathParts.slice(1));
            else
            {
                propName = 'model';
                propPath = new PropPath(pathParts);

                prop = box.componentDesc.properties.get(propName);
            }

            if( !prop )
                return new Error(`Neither property nor event with the name ${propOrEventFullPath} doesn't exist`);

            const propConfig = prop.getPropertyConfig(propPath);
            if( !propConfig )
                return new Error(`Neither property nor event with the name ${propOrEventFullPath} doesn't exist`);

            const isDown = bindingDirection === BindingDirection.Down;
            const isArray = propConfig.isArray;

            const bindWidth = propConfig.width;
            const bindLength = isArray ? itemsCount || -1 : 1;

            const rowCount = isDown ? bindLength : bindWidth;
            const colCount = !isDown ? bindLength : bindWidth;

            commonControllers.bindings.createPropBinding(
                prop,
                propPath,
                workbook.id,
                sheet.id,
                slotLocation,
                framePath,
                rowCount, colCount,
                repeaterFixation,
                bindingDirection,
                useFormat
            );
        }

        return true;
    }
}
